import type { CommentAnchor } from '@peruselab/api-client/discussions';
import type { StructureCorrespondence } from '@peruselab/structure-editor/domain/structureIdRemap';

/** The editor's atoms and bonds a comment can be about (ADR 0016 D2). */
export interface StructureSelection {
  readonly atomIds: readonly string[];
  readonly bondIds: readonly string[];
}

/**
 * The anchor for a workspace selection, in the saved Molecule's own IDs -- the IDs the server
 * checks anchors against (#615: they differ from the editor's when saving reused a Molecule
 * numbered differently). Atoms win over bonds when both are selected: a bond between selected
 * atoms is already in what they point at. Null when nothing is selected.
 */
export function selectionToAnchor(
  selection: StructureSelection,
  correspondence: StructureCorrespondence | null
): CommentAnchor | null {
  const atom = (id: string) => correspondence?.atoms[id] ?? id;
  const bond = (id: string) => correspondence?.bonds[id] ?? id;
  const { atomIds, bondIds } = selection;
  if (atomIds.length === 1) return { kind: 'atom', atomId: atom(atomIds[0]!) };
  if (atomIds.length > 1) return { kind: 'atom-set', atomIds: atomIds.map(atom) };
  if (bondIds.length === 1) return { kind: 'bond', bondId: bond(bondIds[0]!) };
  if (bondIds.length > 1) return { kind: 'bond-set', bondIds: bondIds.map(bond) };
  return null;
}

/** The editor's atoms and bonds an anchor points at -- the way back from {@link selectionToAnchor}. */
export function anchorToSelection(
  anchor: CommentAnchor,
  correspondence: StructureCorrespondence | null
): StructureSelection | null {
  const back = (map: Readonly<Record<string, string>> | undefined) => {
    if (!map) return (id: string) => id;
    const reverse = new Map(Object.entries(map).map(([editor, molecule]) => [molecule, editor]));
    return (id: string) => reverse.get(id) ?? id;
  };
  const atom = back(correspondence?.atoms);
  const bond = back(correspondence?.bonds);
  switch (anchor.kind) {
    case 'atom':
      return { atomIds: [atom(anchor.atomId)], bondIds: [] };
    case 'atom-set':
      return { atomIds: anchor.atomIds.map(atom), bondIds: [] };
    case 'bond':
      return { atomIds: [], bondIds: [bond(anchor.bondId)] };
    case 'bond-set':
      return { atomIds: [], bondIds: anchor.bondIds.map(bond) };
    case 'property':
    case 'statement':
    case 'assertion':
      return null;
  }
}

/** A stable key for an anchor, so the composer can tell a new selection from the same one. */
export function anchorKey(anchor: CommentAnchor | null): string {
  if (!anchor) return '';
  switch (anchor.kind) {
    case 'atom':
      return `atom:${anchor.atomId}`;
    case 'atom-set':
      return `atoms:${anchor.atomIds.join(',')}`;
    case 'bond':
      return `bond:${anchor.bondId}`;
    case 'bond-set':
      return `bonds:${anchor.bondIds.join(',')}`;
    case 'property':
      return `property:${anchor.property}`;
    case 'statement':
      return `statement:${anchor.statementKey}`;
    case 'assertion':
      return `assertion:${anchor.assertionId}`;
  }
}
